/**
 * Flat key/value editor for a character's `traits` JSONB (issue #589).
 *
 * Traits are free-form persona attributes (age, vibe, wardrobe, accent,
 * …). We keep the editor deliberately flat — string keys → string
 * values — because that's all the brief generator needs to fold the
 * persona into the LLM prompt, and a flat shape round-trips cleanly
 * through the API's `Record<string, string|number|boolean>` schema.
 *
 * State lives in the parent as an ordered list of rows so editing a key
 * doesn't reorder or collapse entries (a plain object would). The
 * parent serialises to an object on submit via `rowsToTraits`.
 */

export interface TraitRow {
  key: string;
  value: string;
}

export function rowsToTraits(rows: TraitRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const k = row.key.trim();
    if (k) out[k] = row.value;
  }
  return out;
}

export function traitsToRows(traits: Record<string, string | number | boolean> | undefined): TraitRow[] {
  if (!traits) return [];
  return Object.entries(traits).map(([key, value]) => ({ key, value: String(value) }));
}

export function TraitsEditor({
  rows,
  onChange,
}: {
  rows: TraitRow[];
  onChange: (rows: TraitRow[]) => void;
}) {
  function update(index: number, patch: Partial<TraitRow>) {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  function remove(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }
  function add() {
    onChange([...rows, { key: '', value: '' }]);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.length === 0 && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>
          No traits yet. Add attributes like <code>age</code>, <code>vibe</code>,{' '}
          <code>wardrobe</code>, <code>accent</code> — they get woven into the script prompt.
        </p>
      )}
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="trait (e.g. vibe)"
            value={row.key}
            onChange={(e) => update(i, { key: e.target.value })}
            style={{ flex: '0 0 38%' }}
            spellCheck={false}
            autoCapitalize="off"
          />
          <input
            type="text"
            placeholder="value (e.g. warm, spontaneous)"
            value={row.value}
            onChange={(e) => update(i, { value: e.target.value })}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => remove(i)}
            aria-label={`Remove trait ${row.key || i + 1}`}
            title="Remove trait"
          >
            ✕
          </button>
        </div>
      ))}
      <div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={add}>
          + Add trait
        </button>
      </div>
    </div>
  );
}
